import { ClockIcon, ShareIcon } from '@heroicons/react/24/outline'
import { usePermissions } from 'hooks/usePermissions'
import { useState } from 'react'
import { useSelector } from 'react-redux'
import { loanMoveToServicing } from 'services'
import { svgLoading } from 'stories/assets'
import { Select } from 'stories/components'
import { confirm, openAuditLog } from 'utils'

import { statusSelectFit } from './fitStatusSelect'
import {
  getLoanStatusChangeErrorMessage,
  useAllowedLoanStatuses,
  useLoanStatusChange,
} from './useLoanStatusChange'

export { getLoanStatusChangeErrorMessage, useAllowedLoanStatuses, useLoanStatusChange }

export function LoanStatus(props: any) {
  const { status, loanNumber, servicingPipeline, size = 5, channel: channelProp } = props

  const isNewLoan = loanNumber === 'New'

  const [loading, setLoading] = useState(false)

  const { hasPermission } = usePermissions()

  const openLoanChannel = useSelector((state: any) => state.loanDetail?.loanType)
  // A pipeline row carries its own channel. The open loan's channel must not decide the list
  // for every other row, or a status limited to one channel disappears on the loans that use it.
  const channel = channelProp !== undefined ? channelProp : openLoanChannel
  const options = useAllowedLoanStatuses(status, channel)
  const { changeStatus } = useLoanStatusChange()

  const canMoveToServicing = hasPermission('Loan.Status.ToServicing')

  const changeLoanDetail = async (value: string) => {
    if (isNewLoan) return

    setLoading(true)
    const changed = await changeStatus(Number(loanNumber), value)
    if (changed) props.onChange(value)
    setLoading(false)
  }

  const moveToServicing = async () => {
    if (isNewLoan) return
    const content = <div className="mb-4">Are you sure want to move this loan to Servicing?</div>
    const rlt = await confirm(content)
    if (!rlt) return
    setLoading(true)
    try {
      const res = await loanMoveToServicing(loanNumber)
      if (res.success) {
        props.onMoveServicing()
      }
    } catch {}
    setLoading(false)
  }

  const showHistory = () => {
    if (isNewLoan) return
    const options = {
      table: 'LoanProcess',
      field: 'loanStatus',
      keys: {
        field: 'loanStatus',
        loanNumber: loanNumber,
      },
    }
    openAuditLog(options)
  }

  let sizeClass = 'w-5 h-5'
  let selectSize = 5
  let moveServicingFont = 'font-semibold'
  if (size === 4) {
    sizeClass = 'w-4 h-4'
    selectSize = 3
    moveServicingFont = 'font-normal'
  }

  const selectedLabel = String((options as Record<string, string>)?.[status] ?? status ?? '')
  const { widthCh, textClass } = statusSelectFit(selectedLabel, size)

  return (
    <div className="LoanStatus-container">
      <div className="flex items-center">
        <div className={`${textClass} -mb-4`} style={{ width: `${widthCh}ch` }}>
          <Select
            id={`loan-status-${loanNumber}`}
            size={selectSize}
            title=""
            options={options}
            disabled={loading}
            value={status}
            onChange={(value) => changeLoanDetail(value)}
          />
        </div>
        {loading && <img src={svgLoading} className={`inline ${sizeClass} ml-2 mt-2 mb-2 text-white animate-spin`} />}
        {!loading && (
          <span className="ml-2 text-gray-500 cursor-pointer">
            <ClockIcon className={sizeClass} onClick={showHistory} />
          </span>
        )}
      </div>
      {!servicingPipeline && canMoveToServicing && status === 'funded' && (
        <div
          className="ml-2 mt-1 gap-2 flex items-center text-[12.5px] italic hover:underline cursor-pointer"
          onClick={moveToServicing}
        >
          <span className={moveServicingFont}>Move to Servicing</span>
          <ShareIcon className="w-4 h-4"></ShareIcon>
        </div>
      )}
    </div>
  )
}
